/**
 * MW polysemy guard — SHARED by every engine that reads a capacity figure.
 *
 * "MW" is the most overloaded unit in the corpus. A single report legitimately
 * states the FACILITY's capacity, the utility line's available HEADROOM, on-site
 * BTM generation, a backup generator's rating and a fuel-supply potential — all
 * in MW, often in adjacent sentences. Any engine that groups on the unit alone
 * treats those as one quantity contradicting itself.
 *
 * WHY THIS MODULE EXISTS (2026-08-14, Triple Threat RCA). The guard already
 * lived — inline — in `canonicalFactReconciler` (R-79, cross-report) but never
 * reached `internalNumberReconciler` (tier-2e, within-report). The two engines
 * fell out of lockstep and asserted OPPOSITE truths about the same figure on a
 * live 10 MW edge-datacenter report:
 *
 *   tier-2e   bucketed the project's 10 MW and the line's 26 MW headroom into
 *             one `capacity_mw` key, majority-voted, and offered "Settled → 10"
 *             — a fix that REWRITES grid headroom 26 → 10.
 *   grounding had no capacity fact for a phase-plan site, so it attributed the
 *             project's "10 MW" to `grid.headroomMw` (26) and blocked with
 *             "the only permitted value is 26 MW" — the exact inverse.
 *
 * Settling the audit row wrote the value the gate blocked on. Neither engine
 * was individually unreasonable; they simply did not share this definition.
 * Import it — never re-inline it.
 */

/**
 * Sentences about a NON-FACILITY MW quantity. A capacity extractor must skip
 * a match whose surrounding sentence tests true here.
 *
 * Covers: grid headroom / available / hosting capacity / import allocation,
 * substation ratings, BTM + on-site + backup generation, intent range buckets,
 * and fuel-supply potential ("~50,000 Dth/day … would support 300 MW").
 *
 * NOT covered — deliberately REMOVED 2026-08-14: `range bucket` and `capacity
 * range`. Those are BUCKET-DERIVED vocabulary describing the PROJECT's own
 * declared band, so they are facility-side by construction — and the platform
 * itself instructs the model to write them (`BUCKET_DERIVED_BAND_RULE`,
 * powerStack.ts) and stamps them into the SED. Listing them here dragged the
 * report's echo of its OWN intent to the grid side, where it mismatched grid
 * headroom. Measured: "The project is sized against a declared capacity range
 * of up to 10 MW (bucket-derived)" blocked as `value-mismatch grid.headroomMw
 * exp=26 MW` — the identical sentence without that phrase was clean.
 *
 * The `NN kV` and `subtransmission` alternatives are LOCATORS, not vocabulary:
 * headroom is always attributed to a CONDUCTOR ("26 MW on the 34.5kV line",
 * "hosting capacity of the subtransmission line") whereas a facility's own
 * capacity never is. That structural asymmetry catches the phrasings no word
 * list anticipates. It can drop a facility figure from a genuinely grid-
 * flavoured sentence; that costs one mention in a vote, never a wrong vote.
 *
 * Live provenance for the vocabulary:
 *   Redhead      — "~50 MW of available grid capacity" blocked six reports;
 *                  the gas-transport sentence re-cited 300/50.
 *   TripleThreat — "The user-provided estimate of 26 MW on the 34.5kV line",
 *                  "26 MW of available capacity on the adjacent 34.5kV line",
 *                  "unverified 26 MW of hosting capacity",
 *                  "Unverified Claim of Available Grid Headroom".
 */
export const NON_FACILITY_MW_CONTEXT_RE =
  /(grid (capacity|headroom|import|interconnection)|available (grid )?(capacity|power)|hosting capacity|power indication|spare (capacity|headroom)|headroom|import (capacity|allocation)|interconnection (capacity|allocation|service)|substation (capacity|rating)|subtransmission|\d+(?:\.\d+)?\s*kV\b|BTM (generation|capacity|output)|on-?site generation|backup (power|generation|capacity)|generat(?:or|ion) (capacity|output)|Dth|MMBtu|MMcf|gas (supply|transport|availability)|fuel supply|transport contract|enough (gas|fuel))/i;

/**
 * Substation-distance polysemy — a km figure on a grid-flavoured line that is
 * NOT the site→substation distance. Shared by tier-2e
 * (`internalNumberReconciler`) and the cross-report reconciler
 * (`canonicalFactReconciler`) so both engines assert one truth.
 *
 * Live Redhead feasibility 2026-08-18 (`stne8w`): "a new 3.4 km transmission
 * line to a nearby 230 kV corridor" (developer CAD — a LINE LENGTH, a build
 * quantity) was bucketed against the tier-1 5.2 km Moss Bluff substation
 * distance and hard-held the report. Same class as fiber laterals / IX
 * distances: it merely shares the unit. Matches "<n> km [transmission|new|
 * overhead|underground|double-circuit|<kV>] line/spur/tie-line" and
 * "line/spur … of <n> km".
 */
export const LINE_LENGTH_DISTANCE_CONTEXT_RE =
  /(\b(?:km|mi|miles?|kilometers?)\s+(?:total\s*(?:\([^)]*\)\s*)?)?(?:of\s+)?(?:and\s+[\d,]+(?:\.\d+)?[-\s]*(?:km|mi|miles?|kilometers?)\s+)?(?:(?:transmission|new|proposed|modeled|overhead|underground|radial|double-?circuit|single-?circuit|\d+(?:\.\d+)?\s*kV)\s+){0,4}(?:line|spur|tie-?line|gen-?tie)s?(?:\s+additions?)?\b|\b(?:line|spur|tie-?line|gen-?tie)s?\s+(?:length\s+)?(?:of|spans?|spanning|runs?|running|extends?|extending|measures?|measuring|totall?ing|is|would be|will be)\s+(?:approximately\s+|about\s+|~\s*)?[\d,]+(?:\.\d+)?\s*(?:km|mi|miles?|kilometers?)\b|\b\d+(?:\.\d+)?\s*kV\s*(?:#\d+\s*)?[;,]\s*[\d,]+(?:\.\d+)?[-\s]*(?:km|mi|miles?|kilometers?)\b|\bfiber|\bfibre|\blateral|long-?haul|\bcarrier|\bIX\b|peering|internet exchange|\bgas\b|pipeline)/i;

/**
 * True when `sentence` is talking about an MW quantity that is NOT the
 * facility's own capacity.
 */
export function isNonFacilityMwContext(sentence: string): boolean {
  return NON_FACILITY_MW_CONTEXT_RE.test(sentence);
}

/** Exact scalar field roles preserved by the three report text extractors.
 * Unknown keys retain their existing text; no role is inferred from a value.
 */
export function capacityQuantityFieldLabel(key: string): string | undefined {
  const labels: Readonly<Record<string, string>> = {
    facilityMW: 'Facility demand',
    facilityLoadMW: 'Facility demand',
    generationRequiredMW: 'Upstream generation requirement',
    itLoadMW: 'IT load',
    firmDeliverableMW: 'Firm-deliverable capacity',
    firmDeliverableN1MW: 'Firm-deliverable capacity under N-1',
    requiredFirmMW: 'Required firm capacity',
    largestUnitMW: 'Largest contingency',
    shortfallMW: 'Firm capacity shortfall',
  };
  return Object.prototype.hasOwnProperty.call(labels, key) ? labels[key] : undefined;
}

/**
 * A stated equipment, supply or arithmetic quantity cannot vote on facility
 * demand. Bind these explicit roles to the individual MW mention, rather than
 * dropping a whole paragraph which may also state the actual facility load.
 * Unknown/unqualified MW figures remain eligible for the existing audit.
 */
export function isNonFacilityCapacityMention(
  line: string,
  matchIndex: number | undefined,
  matchLength: number,
): boolean {
  if (typeof matchIndex !== 'number') return false;
  // Keep offsets into the original quote while ignoring Markdown formatting.
  const text = line.replace(/[*_`]/g, ' ');
  let start = 0;
  let end = text.length;
  for (let at = 0; at < text.length; at += 1) {
    if (!/[;!?\n]/.test(text[at]!) && !(text[at] === '.' && !/\d/.test(text[at - 1] ?? '') && /\s/.test(text[at + 1] ?? ''))) continue;
    if (at < matchIndex) start = at + 1;
    else if (at >= matchIndex + matchLength) { end = at; break; }
  }
  // Adjacent quantities have separate roles: 24 MW allocated service minus
  // 20 MW facility demand gives 4 MW design load margin.
  for (const other of text.matchAll(/\b[\d,]+(?:\.\d+)?\s*[-\s]?(?:MW|GW|megawatts?)\b/gi)) {
    const at = other.index!;
    const after = at + other[0].length;
    if (after <= matchIndex && after > start) start = after;
    if (at >= matchIndex + matchLength && at < end) end = at;
  }
  const before = text.slice(start, matchIndex).slice(-90).trim();
  const after = text.slice(matchIndex + matchLength, end).slice(0, 90).trim();
  // An explicitly named facility/phase demand remains a demand even when the
  // surrounding paragraph discusses ELCC, standby, or a reserve requirement.
  if (/^(?:of\s+)?(?:facility|campus|project)\s+(?:load|demand|power requirement|capacity)\b/i.test(after)
    || /\b(?:facility|campus|project)\s+(?:load|demand|power requirement|capacity)\s*(?:is|of|:|\|)?\s*$/i.test(before)
    || /\b(?:facility|campus|project|phase\s+[\div]+)\s+(?:requires?|needs?|demands?)\s*(?:approximately|about|a total of)?\s*$/i.test(before)) return false;
  const roleBefore = /\b(?:firm[- ]deliverable(?:\s+capacity)?|effective load[- ]carrying capacity|ELCC(?:\s+(?:credit|supply|capacity))?|(?:accredited|derated)\s+(?:firm\s+)?(?:supply|capacity|output)|required\s+firm\s+capacity|allocated\s+(?:grid\s+)?(?:service|capacity)|(?:grid|utility)\s+allocation|(?:installed\s+)?(?:diesel|standby|backup)\s+(?:generation|capacity|nameplate|generators?)|(?:diesel|standby|backup)\s+(?:generator\s+sets?|gensets?)|(?:individual|per[- ]unit|each)\s+(?:generator|genset|turbine|UPS|unit)(?:\s+(?:rating|capacity))?|(?:generator|genset|turbine|UPS)\s+(?:rating|capacity|output)|(?:Cat(?:erpillar)?|Cummins|Kohler|MTU)\s+(?:\S+\s*){0,3}|(?:design\s+)?(?:(?:critical[- ])?load\s+|reserve\s+|capacity\s+)?(?:margin|shortfall|deficit|surplus)|IT\s+load|(?:thermal|cooling|heat[- ]rejection)\s+(?:duty|capacity)|N-1\s+net\s+after(?:\s+[\d,]+(?:\.\d+)?\s*(?:MW|GW)\s+loss)?|loss|(?:feeds?|feeders?)\s+is|Field:\s*grid\.estimatedCapacityMW\b[^;]*\|\s*Value)\b[^;]{0,35}$/i;
  const roleAfter = /^(?:of\s+)?(?:(?:installed|duty|firm[- ]capacity|design|one[- ]set[- ]out|MC)\s+)*(?:diesel\b|standby\b|backup\b|(?:Cat(?:erpillar)?|Cummins|Kohler|MTU)\s+(?:\S+\s+){0,2}(?:diesel|generators?|gensets?)\b|generator(?:s|\s+rating)?\b|gensets?\b|turbine\s+rating\b|UPS\s+(?:rating|capacity)\b|firm(?:\s+|[-])deliverable\b|(?:grid[- ]import\s+)?ELCC\b|(?:(?:critical[- ])?load\s+|reserve\s+|capacity\s+|design\s+)*(?:margin|shortfall|deficit|surplus)\b|over\s+the\s+(?:critical\s+load|reserve[- ]padded\s+requirement)\b|allocated\s+service\b|grid\s+allocation\b|(?:critical\s+)?IT\b|thermal\b|cooling\s+(?:duty|capacity)\b|heat[- ]rejection\s+(?:duty|capacity)\b|normal\s+prime\s+generation\b|loss(?:\s*:|\b)|duty\b|(?:\)\s*)?(?:(?:standby|backup|diesel)\s+)+(?:generators?|gensets?|units?|duty)\b|installed\s*\($)/i;
  // Supply/loss-adjusted roles in persisted River Ridge prose and typed
  // breakdowns. Keep these directly attached to this quantity; another MW
  // value already bounds before/after, so its role cannot be borrowed.
  const explicitSupplyBefore = /\b(?:upstream generation (?:requirement|required)|generation required MW|IT load(?: MW)?|normal net output|(?:(?:one[- ]feed[- ]out|modeled|N-1)\s+)*surviving\s+(?:supply|allocated service)|aggregate(?: installed)? (?:feed|feeder) (?:capacity|rating|envelope)|aggregate[- ]capacity[- ]equivalent duty|(?:required firm capacity|reserve[- ]padded (?:target|requirement)|reserve target)|largest contingency|(?:site[- ]deliverable\s+)?(?:firm\s+)?load[- ]service (?:allocation|capacity))\s*(?:is|of|:|\||\()?\s*$/i;
  const agreementBefore = /\b(?:electric\s+)?load[- ]service agreement\b[^;]{0,45}\bfor\s*$/i;
  const explicitSupplyAfter = /^(?:of\s+)?(?:upstream generation (?:requirement|required)\b|(?:(?:utility|grid|primary)\s+)?(?:feeds?|feeders?|paths?)(?:\s+circuits?)?\b|aggregate (?:installed )?(?:rating|(?:feed(?:er)? )?(?:capacity|envelope))\b|(?:(?:modeled|N-1|one[- ]feed[- ]out)\s+)*surviving\s+(?:supply|allocated service)\b|(?:required firm|reserve[- ]padded (?:target|requirement))\b|(?:primary\s+)?firm[- ]capacity\s+(?:shortfall|requirement|target)\b|requirement during (?:this|the) contingency\b|(?:(?:(?:firm|customer|site)\s+)?(?:load[- ]service|service)\s+(?:allocation|capacity)|(?:firm|customer|site)\s+allocation)\b|grid\s+import\b|(?:modeled\s+)?(?:path\s+|transformer\s+|line\s+)?loss(?:es)?\b|conversion\s+reference\b|under\s+the\s+N-1\s+criterion\b|(?:are|is) (?:a |an )?supply envelope\b)/i;
  const firmSupplyComparison = /\bfirm\s*$/i.test(before) && /^covers?\b/i.test(after);
  return roleBefore.test(before) || roleAfter.test(after)
    || explicitSupplyBefore.test(before) || agreementBefore.test(before)
    || explicitSupplyAfter.test(after) || firmSupplyComparison;
}

/* ──────────────────────────────────────────────────────────────────────
 * Comparison-BOUND prefix guard — SYNTAX-level, subject-AGNOSTIC
 * ────────────────────────────────────────────────────────────────────── */

/**
 * A figure directly prefixed by a comparison operator or comparative phrase
 * ("<50 MW", "less than 50 MW", "up to 10 MW") states a BOUND, not a point
 * assertion. Extracting it as a value claim manufactures a conflict against
 * the report's real cited value. Sibling of the dash-range endpoint guard
 * ("200–500 MW" is a band): both veto by the SHAPE of the text immediately
 * before the number — never by an inferred subject. Subject pre-filtering is
 * the classifier that regressed production and was reverted (`e7f9bf8b2`);
 * this guard is safe precisely because it needs no opinion about WHOSE
 * quantity the number is.
 *
 * Live provenance (Pronghorn PRB, 2026-08-17): "Initiate discussions with
 * Powder River Energy Corporation for a limited-capacity interconnection
 * (<50 MW)" — the 50 is an interconnection BOUND, yet all three audit
 * engines extracted it as a facility-capacity claim and raised
 * "REPORT CITES 50 → VERIFIED 1,000" against the same report's own 1,000 MW
 * summary. The subject guard above cannot catch it ("interconnection (<50"
 * carries none of its vocabulary in guard order) and must not be bent to.
 *
 * Deliberately ABSENT from the word list: "over" / "exceeding" — "spread
 * over 1,170 acres" is a genuine point claim of acreage, and this predicate
 * is applied unit-agnostically by the Tier-3 extractor.
 */
const WORD_BOUND_PREFIX_RE =
  /\b(?:less than|fewer than|no more than|at most|under|below|up to|at least|more than|greater than|in excess of)\s*$/i;

/** Trailing comparison operator, e.g. "(<", "≥ ", ">=". */
const OPERATOR_BOUND_PREFIX_RE = /([<>≤≥])=?\s*$/;

/**
 * True when the value starting at `matchIndex` in `line` is prefixed by a
 * comparison bound and must not enter a fact's value set — as a claim OR as
 * corroboration. Dropping a mention is strictly finding-REDUCING in every
 * engine that calls this (a mention can only create or vote on a conflict),
 * so the guard can never flip a verdict, only silence a manufactured one.
 *
 * `matchIndex` undefined → false: without a position we cannot prove the
 * bound, and keeping the mention at worst yields a finding a human reviews.
 */
export function isComparisonBoundPrefix(line: string, matchIndex: number | undefined): boolean {
  if (typeof matchIndex !== 'number') return false;
  const prefix = line.slice(0, matchIndex);
  if (WORD_BOUND_PREFIX_RE.test(prefix)) return true;
  const op = prefix.match(OPERATOR_BOUND_PREFIX_RE);
  if (!op) return false;
  // A '>' with only whitespace before it is a markdown BLOCKQUOTE marker,
  // not a comparison ("> 500 MW of capacity…" quotes a claim; it does not
  // bound one). A bound genuinely at line start (">500 MW") is missed —
  // rare, and missing it merely keeps today's behaviour for that mention.
  if (op[1] === '>' && prefix.slice(0, op.index).trim() === '') return false;
  return true;
}
